import { useState, useEffect, useRef } from 'react';
import { writeOrgFile, deleteOrgFile, listOrgCollection } from '../../storage/FileSystemAdapter';
import { appendChangeLog } from '../../utils/changeLog';
import { SUPPLIER_TYPES, SUPPLIER_STATUSES, CRITICALITY_LEVELS } from '../../utils/enums';
import PeopleSelector from '../shared/PeopleSelector';
import TagInput from '../shared/TagInput';
import ConfirmDialog from '../shared/ConfirmDialog';
import SaveErrorDialog from '../shared/SaveErrorDialog';
import { BG_CONTENT, BTN_PRIMARY, BTN_SECONDARY, BTN_DESTRUCTIVE_OUTLINE, TEXT_MUTED, TEXT_DISABLED, BORDER_DEFAULT, TEXT_HEADING, BG_SURFACE, BRAND_PRIMARY_DARK, ERROR_TEXT, ERROR_BG, ERROR_BORDER, FIELD_ERROR_BORDER, UNSAVED_AMBER, SURFACE_WHITE, FONT_SECTION_TITLE, FONT_BODY, FONT_BODY_STRONG, FONT_LABEL, FONT_SMALL, FONT_TINY, FONT_BUTTON, STATUS_INFO } from '../../theme';
import InfoBadge from '../shared/InfoBadge';
import EntitySelector from '../shared/EntitySelector';

// ── CONSTANTS ─────────────────────────────────────────────────────────────────
const REVIEW_OUTCOMES   = ['Approved','Approved with conditions','Under review','Rejected'];

// ── I18N ──────────────────────────────────────────────────────────────────────
const T = {
  es: {
    titleCreate:          'Nuevo proveedor',
    titleEdit:            'Editar proveedor',
    titleView:            'Ver proveedor',
    s1:                   'Identificación',
    s2:                   'Contacto',
    s3:                   'Servicios y acceso a datos',
    s4:                   'Contrato',
    s5:                   'Revisión periódica',
    s6:                   'Relaciones',
    s7:                   'Ciclo de vida',
    fRef:                 'Referencia',
    fName:                'Nombre',
    fType:                'Tipo',
    fCriticality:         'Criticidad',
    fDescription:         'Descripción',
    fAddress:             'Dirección',
    fCountry:             'País',
    fContactName:         'Nombre de contacto',
    fContactEmail:        'Email de contacto',
    fOwner:               'Responsable interno',
    fServicesProvided:    'Servicios proporcionados',
    fPersonalDataProcessed: 'El proveedor procesa datos personales',
    fDataProcessed:       'Tipos de datos procesados',
    fDataProcessingPurpose:'Finalidad del tratamiento',
    fTransferMechanism:   'Mecanismo de transferencia internacional',
    fContractExists:      'Existe contrato formalizado',
    fNdaSigned:           'NDA firmado',
    fDpaSigned:           'DPA firmado',
    fSecurityReq:         'Requisitos de seguridad incluidos en el contrato',
    fContractRef:         'Referencia del contrato',
    fContractStart:       'Fecha de inicio del contrato',
    fContractExpiry:      'Fecha de vencimiento del contrato',
    fIncidentNotificationSla: 'SLA notificación de incidentes (horas)',
    fCertifications:      'Certificaciones',
    fReviewFrequency:     'Frecuencia de revisión (meses)',
    fLastReviewDate:      'Fecha de última revisión',
    fLastReviewOutcome:   'Resultado de la última revisión',
    fNextReviewDate:      'Próxima revisión (calculada automáticamente)',
    fSubprocessorIds:     'Subencargados',
    fEvidenceIds:         'Evidencias vinculadas',
    fStatus:              'Estado',
    fExitPlanExists:      'Plan de salida documentado',
    fAuditRights:         'Derecho contractual de auditoría',
    fAuditRightsCritical: 'Crítico: exigido para proveedores de criticidad Alta',
    fNotes:               'Notas',
    optional:             '(opcional)',
    btnSave:              'Guardar',
    btnCancel:            'Cancelar',
    btnDelete:            'Eliminar proveedor',
    btnConfirmDelete:     '¿Confirmar eliminación?',
    btnCancelDelete:      'No, cancelar',
    saving:               'Guardando…',
    saveOk:               '✓ Guardado',
    errorRequired:        'Campo obligatorio',
    errorSave:            'Error al guardar. Inténtalo de nuevo.',
    errorDelete:          'Error al eliminar.',
    required:             '*',
    requiredLegend:       '* Campos obligatorios',
    servicePlaceholder:   'Añadir servicio y pulsar Enter…',
    dataTypePlaceholder:  'Añadir tipo de dato y pulsar Enter…',
    purposePlaceholder:   'p.ej. alojamiento de datos de clientes',
    transferPlaceholder:  'p.ej. Cláusulas contractuales tipo (SCCs)',
    slaPlaceholder:       'p.ej. 24',
    certPlaceholder:      'Añadir certificación y pulsar Enter…',
    subprocessorPlaceholder: 'Añadir ID de subencargado y pulsar Enter…',
    noRiskIds:            'Sin riesgos vinculados',
    noControlIds:         'Sin controles vinculados',
    noEvidenceIds:        'Sin evidencias vinculadas',
    evidencePlaceholder:  'Añadir ID de evidencia y pulsar Enter…',
    noOutcome:            'Sin revisar',
    generating:           'Generando…',
    unsavedLabel:         'Cambios sin guardar',
    ttDataProcessingPurpose: 'Describe para qué usa este proveedor los datos (ej. alojamiento, procesamiento de pagos, análisis). Necesario para el registro de actividades de tratamiento.',
    ttTransferMechanism:     'Base legal para transferir datos fuera del EEE. Ej. Cláusulas Contractuales Tipo (CCT), decisión de adecuación, BCR.',
    ttSecurityReq:           'El contrato incluye cláusulas específicas sobre controles de seguridad, gestión de incidentes y obligaciones de confidencialidad.',
    ttDpaSigned:             'Acuerdo de Tratamiento de Datos (ATD/DPA) firmado. Obligatorio bajo GDPR cuando el proveedor trata datos personales en nombre de tu organización.',
    ttAuditRights:           'El contrato permite a tu organización auditar al proveedor o exigir certificaciones de seguridad (ej. ISO 27001, SOC 2). Requerido por ISO 27001 cláusula 8.4.',
    ttExitPlanExists:        'Existe un procedimiento documentado para terminar la relación con este proveedor: migración de datos, revocación de accesos y eliminación segura.',
    typeLabels: {
      Cloud:                   'Cloud',
      SaaS:                    'SaaS',
      'IT Services':           'Servicios TI',
      'Professional Services': 'Servicios profesionales',
      Hardware:                'Hardware',
      Other:                   'Otro',
    },
    criticalityLabels: {
      Critical: 'Crítico',
      High:     'Alta',
      Medium:   'Media',
      Low:      'Baja',
    },
    statusLabels: {
      'Active':       'Activo',
      'Under Review': 'En revisión',
      'Terminated':   'Terminado',
    },
  },
  en: {
    titleCreate:          'New supplier',
    titleEdit:            'Edit supplier',
    titleView:            'View supplier',
    s1:                   'Identification',
    s2:                   'Contact',
    s3:                   'Services and data access',
    s4:                   'Contract',
    s5:                   'Periodic review',
    s6:                   'Relationships',
    s7:                   'Lifecycle',
    fRef:                 'Reference',
    fName:                'Name',
    fType:                'Type',
    fCriticality:         'Criticality',
    fDescription:         'Description',
    fAddress:             'Address',
    fCountry:             'Country',
    fContactName:         'Contact name',
    fContactEmail:        'Contact email',
    fOwner:               'Internal responsible',
    fServicesProvided:    'Services provided',
    fPersonalDataProcessed: 'Supplier processes personal data',
    fDataProcessed:       'Types of data processed',
    fDataProcessingPurpose:'Data processing purpose',
    fTransferMechanism:   'International transfer mechanism',
    fContractExists:      'Formal contract in place',
    fNdaSigned:           'NDA signed',
    fDpaSigned:           'DPA signed',
    fSecurityReq:         'Security requirements included in contract',
    fContractRef:         'Contract reference',
    fContractStart:       'Contract start date',
    fContractExpiry:      'Contract expiry date',
    fIncidentNotificationSla: 'Incident notification SLA (hours)',
    fCertifications:      'Certifications',
    fReviewFrequency:     'Review frequency (months)',
    fLastReviewDate:      'Last review date',
    fLastReviewOutcome:   'Last review outcome',
    fNextReviewDate:      'Next review (auto-calculated)',
    fSubprocessorIds:     'Sub-processors',
    fEvidenceIds:         'Linked evidence',
    fStatus:              'Status',
    fExitPlanExists:      'Exit plan documented',
    fAuditRights:         'Contractual right to audit',
    fAuditRightsCritical: 'Critical: required for high-criticality suppliers',
    fNotes:               'Notes',
    optional:             '(optional)',
    btnSave:              'Save',
    btnCancel:            'Cancel',
    btnDelete:            'Delete supplier',
    btnConfirmDelete:     'Confirm delete?',
    btnCancelDelete:      'No, cancel',
    saving:               'Saving…',
    saveOk:               '✓ Saved',
    errorRequired:        'Required',
    errorSave:            'Save failed. Please try again.',
    errorDelete:          'Delete failed.',
    required:             '*',
    requiredLegend:       '* Required fields',
    servicePlaceholder:   'Add service and press Enter…',
    dataTypePlaceholder:  'Add data type and press Enter…',
    purposePlaceholder:   'e.g. hosting of customer data',
    transferPlaceholder:  'e.g. Standard Contractual Clauses (SCCs)',
    slaPlaceholder:       'e.g. 24',
    certPlaceholder:      'Add certification and press Enter…',
    subprocessorPlaceholder: 'Add sub-processor ID and press Enter…',
    noRiskIds:            'No linked risks',
    noControlIds:         'No linked controls',
    noEvidenceIds:        'No linked evidence',
    evidencePlaceholder:  'Add evidence ID and press Enter…',
    noOutcome:            'Not reviewed',
    generating:           'Generating…',
    unsavedLabel:         'Unsaved changes',
    ttDataProcessingPurpose: 'Describes what this supplier does with data (e.g. hosting, payment processing, analytics). Required for the record of processing activities.',
    ttTransferMechanism:     'Legal basis for transferring data outside the EEA. E.g. Standard Contractual Clauses (SCCs), adequacy decision, BCR.',
    ttSecurityReq:           'The contract includes specific clauses on security controls, incident management and confidentiality obligations.',
    ttDpaSigned:             'Data Processing Agreement signed. Mandatory under GDPR when the supplier processes personal data on behalf of your organization.',
    ttAuditRights:           'The contract allows your organization to audit the supplier or require security certifications (e.g. ISO 27001, SOC 2). Required by ISO 27001 clause 8.4.',
    ttExitPlanExists:        'A documented procedure exists for ending the relationship with this supplier: data migration, access revocation and secure deletion.',
    typeLabels: {
      Cloud:                   'Cloud',
      SaaS:                    'SaaS',
      'IT Services':           'IT Services',
      'Professional Services': 'Professional Services',
      Hardware:                'Hardware',
      Other:                   'Other',
    },
    criticalityLabels: {
      Critical: 'Critical',
      High:     'High',
      Medium:   'Medium',
      Low:      'Low',
    },
    statusLabels: {
      'Active':       'Active',
      'Under Review': 'Under Review',
      'Terminated':   'Terminated',
    },
  },
};

// ── HELPERS ───────────────────────────────────────────────────────────────────
function todayStr() { return new Date().toISOString().slice(0, 10); }
function yyyymmdd() { return todayStr().replace(/-/g, ''); }

function calcNextReviewDate(lastReviewDate, reviewFrequencyMonths) {
  const months = Number(reviewFrequencyMonths) || 12;
  const base   = lastReviewDate ? new Date(lastReviewDate) : new Date();
  const d      = new Date(base);
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

async function generateSupplierIds(orgId) {
  let suppliers = [];
  try { suppliers = await listOrgCollection(orgId, 'suppliers'); } catch { /* empty */ }

  const day       = yyyymmdd();
  const dayPrefix = `sup-${day}-`;
  let maxFileSeq  = 0;
  suppliers.forEach((s) => {
    if (s.id && s.id.startsWith(dayPrefix)) {
      const n = parseInt(s.id.slice(dayPrefix.length), 10);
      if (!isNaN(n) && n > maxFileSeq) maxFileSeq = n;
    }
  });
  const fileId = `${dayPrefix}${String(maxFileSeq + 1).padStart(3, '0')}`;

  const year = new Date().getFullYear();
  const yearPrefix = `SUP-${year}-`;
  let maxRefSeq = 0;
  suppliers.forEach((s) => {
    if (s.ref && s.ref.startsWith(yearPrefix)) {
      const n = parseInt(s.ref.slice(yearPrefix.length), 10);
      if (!isNaN(n) && n > maxRefSeq) maxRefSeq = n;
    }
  });
  const ref = `${yearPrefix}${String(maxRefSeq + 1).padStart(3, '0')}`;

  return { fileId, ref };
}

// ── INITIAL STATE ─────────────────────────────────────────────────────────────
const EMPTY_FORM = {
  name: '', type: 'Cloud', criticality: 'Medium', description: '',
  address: '', country: '', contactName: '', contactEmail: '',
  owner: '',
  servicesProvided: [],
  personalDataProcessed: false,
  dataProcessed: [],
  dataProcessingPurpose: '',
  transferMechanism: '',
  securityRequirementsIncluded: false,
  contractExists: false,
  ndaSigned: false,
  dpaSigned: false,
  contractRef: '', contractStartDate: '', contractExpiry: '',
  incidentNotificationSlaHours: '',
  certifications: [],
  reviewFrequencyMonths: 12,
  lastReviewDate: '', lastReviewOutcome: '',
  nextReviewDate: '',
  status: 'Active', exitPlanExists: false, auditRights: false, notes: '',
  evidenceIds: [],
  subprocessorIds: [],
  norms: [], tags: [],
};

function supplierToForm(s) {
  return {
    name:                        s.name                         || '',
    type:                        SUPPLIER_TYPES.includes(s.type) ? s.type : 'Other',
    criticality:                 s.criticality                  || 'Medium',
    description:                 s.description                  || '',
    address:                     s.address                      || '',
    country:                     s.country                      || '',
    contactName:                 s.contactName                  || '',
    contactEmail:                s.contactEmail                 || '',
    owner:                       s.owner                        || '',
    servicesProvided:            Array.isArray(s.servicesProvided) ? s.servicesProvided : [],
    personalDataProcessed:       !!s.personalDataProcessed,
    dataProcessed:               Array.isArray(s.dataProcessed) ? s.dataProcessed : [],
    dataProcessingPurpose:       s.dataProcessingPurpose        || '',
    transferMechanism:           s.transferMechanism            || '',
    securityRequirementsIncluded:!!s.securityRequirementsIncluded,
    contractExists:              !!s.contractExists,
    ndaSigned:                   !!s.ndaSigned,
    dpaSigned:                   !!s.dpaSigned,
    contractRef:                 s.contractRef                  || '',
    contractStartDate:           s.contractStartDate            || '',
    contractExpiry:              s.contractExpiry               || '',
    incidentNotificationSlaHours:s.incidentNotificationSlaHours != null ? String(s.incidentNotificationSlaHours) : '',
    certifications:              Array.isArray(s.certifications) ? s.certifications : [],
    reviewFrequencyMonths:       s.reviewFrequencyMonths        || 12,
    lastReviewDate:              s.lastReviewDate               || '',
    lastReviewOutcome:           s.lastReviewOutcome            || '',
    nextReviewDate:              s.nextReviewDate || calcNextReviewDate(s.lastReviewDate, s.reviewFrequencyMonths),
    status:                      SUPPLIER_STATUSES.includes(s.status) ? s.status : 'Active',
    exitPlanExists:              typeof s.exitPlanExists === 'boolean' ? s.exitPlanExists : false,
    auditRights:                 typeof s.auditRights === 'boolean' ? s.auditRights : false,
    notes:                       s.notes                        || '',
    evidenceIds:                 Array.isArray(s.evidenceIds) ? s.evidenceIds : [],
    subprocessorIds:             Array.isArray(s.subprocessorIds) ? s.subprocessorIds : [],
    norms:                       Array.isArray(s.norms) ? s.norms : [],
    tags:                        Array.isArray(s.tags) ? s.tags : [],
  };
}

// ── SHARED STYLES ─────────────────────────────────────────────────────────────
const labelSt = {
  ...FONT_SMALL,
  display: 'block', color: TEXT_MUTED,
  letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '0.3rem',
};
const inputSt = (hasErr) => ({
  ...FONT_BODY,
  width: '100%', padding: '0.55rem 0.7rem',
  border: `1px solid ${hasErr ? FIELD_ERROR_BORDER : BORDER_DEFAULT}`,
  borderRadius: 6, background: SURFACE_WHITE,
  color: TEXT_HEADING, outline: 'none', boxSizing: 'border-box',
});
const textareaSt = (hasErr) => ({ ...inputSt(hasErr), resize: 'vertical', minHeight: 72 });
const selectSt = { ...inputSt(false), cursor: 'pointer' };
const sectionHeading = {
  ...FONT_BODY_STRONG,
  color: TEXT_HEADING,
  margin: '0 0 1rem', paddingBottom: '0.5rem',
  borderBottom: `1px solid ${BORDER_DEFAULT}`,
};
const fieldWrap = { marginBottom: '1rem' };
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1.5rem' };

function ErrMsg({ msg }) {
  if (!msg) return null;
  return (
    <p style={{ ...FONT_SMALL, color: ERROR_TEXT, margin: '0.2rem 0 0' }}>
      {msg}
    </p>
  );
}

// ── COMPONENT ─────────────────────────────────────────────────────────────────
export default function SupplierForm({ supplier, mode, org, lang, onSave, onCancel, onFormChange, saveRequestTick, dirty }) {
  const t      = T[lang] || T.en;
  const isEdit = !!supplier;
  const isView = mode === 'view';
  const [currentMode, setCurrentMode] = useState(mode || (isEdit ? 'edit' : 'create'));
  const [savedRecord, setSavedRecord] = useState(isEdit ? supplier : null);
  const hasChangedRef = useRef(false);

  const [form, setForm] = useState(
    () => isEdit ? supplierToForm(supplier) : {
      ...EMPTY_FORM,
      nextReviewDate: calcNextReviewDate('', 12),
    }
  );
  const [fileId,            setFileId]            = useState(isEdit ? supplier.id  : null);
  const [ref,               setRef]               = useState(isEdit ? supplier.ref : null);
  const [errors,            setErrors]            = useState({});
  const [saveState,         setSaveState]         = useState('idle');
  const [saveErrorOpen,     setSaveErrorOpen]     = useState(false);
  const [confirmState,      setConfirmState]      = useState(null);
  const [deleteError,       setDeleteError]       = useState(null);
  const [supplierOptions,   setSupplierOptions]   = useState([]);

  useEffect(() => {
    listOrgCollection(org.id, 'suppliers')
      .then((list) => setSupplierOptions(list.map((s) => ({ id: s.id, label: s.name || s.ref || s.id }))))
      .catch(() => setSupplierOptions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org.id]);

  // Generate IDs on create
  useEffect(() => {
    if (isEdit) return;
    generateSupplierIds(org.id)
      .then(({ fileId: fid, ref: r }) => { setFileId(fid); setRef(r); })
      .catch(() => { setFileId(`sup-${yyyymmdd()}-001`); setRef(`SUP-${new Date().getFullYear()}-001`); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-calculate nextReviewDate when lastReviewDate or reviewFrequencyMonths changes
  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      nextReviewDate: calcNextReviewDate(prev.lastReviewDate, prev.reviewFrequencyMonths),
    }));
  }, [form.lastReviewDate, form.reviewFrequencyMonths]); // eslint-disable-line react-hooks/exhaustive-deps

  const markChange = () => {
    if (!isView && !hasChangedRef.current && onFormChange) {
      hasChangedRef.current = true;
      onFormChange();
    }
  };

  const sf  = (field) => (val) => { markChange(); setForm((p) => ({ ...p, [field]: val })); };
  const hic = (field) => (e)   => sf(field)(e.target.value);

  const validate = () => {
    const errs = {};
    if (!form.name.trim())   errs.name        = t.errorRequired;
    if (!form.owner)         errs.owner       = t.errorRequired;
    if (!form.type)          errs.type        = t.errorRequired;
    if (!form.criticality)   errs.criticality = t.errorRequired;
    if (!form.status)        errs.status      = t.errorRequired;
    return errs;
  };

  const handleSave = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    if (!fileId || !ref) return;
    setSaveState('saving');
    const now = new Date().toISOString();
    try {
      const payload = {
        id:                          fileId,
        orgId:                       org.id,
        ref,
        name:                        form.name.trim(),
        type:                        form.type,
        criticality:                 form.criticality,
        description:                 form.description.trim(),
        address:                     form.address.trim() || null,
        country:                     form.country.trim() || null,
        owner:                       form.owner,
        contactName:                 form.contactName.trim(),
        contactEmail:                form.contactEmail.trim() || null,
        servicesProvided:            form.servicesProvided,
        personalDataProcessed:       form.personalDataProcessed,
        dataProcessed:               form.dataProcessed,
        dataProcessingPurpose:       form.dataProcessingPurpose.trim() || null,
        transferMechanism:           form.transferMechanism.trim() || null,
        securityRequirementsIncluded:form.securityRequirementsIncluded,
        contractExists:              form.contractExists,
        ndaSigned:                   form.ndaSigned,
        dpaSigned:                   form.dpaSigned,
        contractRef:                 form.contractRef.trim() || null,
        contractStartDate:           form.contractStartDate || null,
        contractExpiry:              form.contractExpiry || null,
        incidentNotificationSlaHours:form.incidentNotificationSlaHours === '' ? null : (Number(form.incidentNotificationSlaHours) || null),
        certifications:              form.certifications,
        reviewFrequencyMonths:       Number(form.reviewFrequencyMonths) || 12,
        lastReviewDate:              form.lastReviewDate || null,
        lastReviewOutcome:           form.lastReviewOutcome || null,
        nextReviewDate:              form.nextReviewDate,
        evidenceIds:                 form.evidenceIds,
        subprocessorIds:             form.subprocessorIds,
        norms:                       form.norms,
        tags:                        form.tags,
        status:                      form.status,
        exitPlanExists:              form.exitPlanExists,
        auditRights:                 form.auditRights,
        notes:                       form.notes.trim(),
        createdAt:                   savedRecord?.createdAt   || now,
        updatedAt:                   now,
      };
      payload.changeLog = savedRecord ? appendChangeLog(savedRecord, payload, org.consultantPersonId || 'system') : [];
      await writeOrgFile(org.id, `suppliers/${fileId}.json`, payload);
      setSavedRecord(payload);
      if (currentMode !== 'edit') setCurrentMode('edit');
      onSave(fileId);
      setSaveState('ok');
      setTimeout(() => setSaveState('idle'), 2000);
    } catch {
      setSaveState('error');
      // §11 rule 37 — blocking dialog on per-entity save failure.
      setSaveErrorOpen(true);
      setTimeout(() => setSaveState('idle'), 3000);
    }
  };

  // §11 rule 37 — discard reverts to last saved snapshot and exits the form.
  const handleDiscardChanges = () => {
    setSaveErrorOpen(false);
    if (savedRecord) {
      setForm(supplierToForm(savedRecord));
      hasChangedRef.current = false;
      setErrors({});
    }
    onCancel();
  };
  const handleRetrySave = () => {
    setSaveErrorOpen(false);
    handleSave();
  };

  useEffect(() => {
    if (saveRequestTick > 0) handleSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveRequestTick]);

  const handleDelete = async () => {
    try {
      await deleteOrgFile(org.id, `suppliers/${fileId}.json`);
      onSave(null);
    } catch {
      setDeleteError(t.errorDelete);
      setConfirmState(null);
    }
  };

  const disabled = saveState === 'saving' || isView;

  const btnPrimary = {
    ...BTN_PRIMARY, ...FONT_BUTTON,
    background: saveState === 'saving' ? TEXT_MUTED : saveState === 'ok' ? BRAND_PRIMARY_DARK : BTN_PRIMARY.background,
    border: 'none', borderRadius: 8,
    padding: '0.55rem 1.25rem', cursor: disabled ? 'default' : 'pointer',
  };
  const btnSecondary = {
    ...BTN_SECONDARY, ...FONT_BUTTON,
    borderRadius: 8,
    padding: '0.55rem 1.25rem', cursor: disabled ? 'default' : 'pointer',
  };

  return (
    <div style={{ background: BG_SURFACE, borderRadius: 12, border: `1px solid ${BORDER_DEFAULT}`, padding: '2rem' }}>
      <ConfirmDialog
        open={confirmState !== null}
        message={t.btnConfirmDelete}
        onConfirm={handleDelete}
        onCancel={() => setConfirmState(null)}
        confirmLabel={confirmState?.confirmLabel}
      />

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ ...FONT_SECTION_TITLE, color: TEXT_HEADING, margin: 0 }}>
          {isView ? t.titleView : (currentMode === 'edit' ? t.titleEdit : t.titleCreate)}
          {ref && (
            <span style={{ ...FONT_LABEL, color: TEXT_MUTED, marginLeft: '0.75rem' }}>
              {ref}
            </span>
          )}
          {!ref && !isView && currentMode !== 'edit' && (
            <span style={{ ...FONT_SMALL, color: TEXT_DISABLED, marginLeft: '0.5rem' }}>
              {t.generating}
            </span>
          )}
        </h2>
        {!isView && (
          <p style={{ ...FONT_SMALL, color: TEXT_DISABLED, margin: '0.35rem 0 0' }}>
            {t.requiredLegend}
          </p>
        )}
      </div>

      <fieldset disabled={disabled} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      {/* ── Section 1: Identification ──────────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}>{t.s1}</h3>
        <div style={grid2}>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fName} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
            <input value={form.name} onChange={hic('name')} disabled={disabled} style={inputSt(!!errors.name)} />
            <ErrMsg msg={errors.name} />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fType} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
            <select value={form.type} onChange={hic('type')} disabled={disabled} style={selectSt}>
              {SUPPLIER_TYPES.map((v) => <option key={v} value={v}>{t.typeLabels[v]}</option>)}
            </select>
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fCriticality} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
            <select value={form.criticality} onChange={hic('criticality')} disabled={disabled} style={selectSt}>
              {CRITICALITY_LEVELS.map((v) => <option key={v} value={v}>{t.criticalityLabels[v]}</option>)}
            </select>
          </div>
          <div style={{ ...fieldWrap, gridColumn: 'span 2' }}>
            <label style={labelSt}>{t.fDescription}</label>
            <textarea value={form.description} onChange={hic('description')} disabled={disabled} style={textareaSt(false)} />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>
              {t.fAddress}{' '}
              <span style={{ ...FONT_TINY, color: TEXT_DISABLED, textTransform: 'none', letterSpacing: 0 }}>
                {t.optional}
              </span>
            </label>
            <input value={form.address} onChange={hic('address')} disabled={disabled} style={inputSt(false)} />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>
              {t.fCountry}{' '}
              <span style={{ ...FONT_TINY, color: TEXT_DISABLED, textTransform: 'none', letterSpacing: 0 }}>
                {t.optional}
              </span>
            </label>
            <input value={form.country} onChange={hic('country')} disabled={disabled} style={inputSt(false)} />
          </div>
        </div>
      </div>

      {/* ── Section 2: Contact ─────────────────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}>{t.s2}</h3>
        <div style={grid2}>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fOwner} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
            <PeopleSelector orgId={org.id} lang={lang} value={form.owner} onChange={sf('owner')} disabled={disabled} />
            <ErrMsg msg={errors.owner} />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fContactName}</label>
            <input value={form.contactName} onChange={hic('contactName')} disabled={disabled} style={inputSt(false)} />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>
              {t.fContactEmail}{' '}
              <span style={{ ...FONT_TINY, color: TEXT_DISABLED, textTransform: 'none', letterSpacing: 0 }}>
                {t.optional}
              </span>
            </label>
            <input type="email" value={form.contactEmail} onChange={hic('contactEmail')} disabled={disabled} style={inputSt(false)} />
          </div>
        </div>
      </div>

      {/* ── Section 3: Services & Data Access ─────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}>{t.s3}</h3>
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fServicesProvided}</label>
          <TagInput
            tags={form.servicesProvided}
            onAdd={(v) => sf('servicesProvided')([...form.servicesProvided, v])}
            onRemove={(v) => sf('servicesProvided')(form.servicesProvided.filter((x) => x !== v))}
            onUpdate={(oldValue, newValue) => sf('servicesProvided')(form.servicesProvided.map((x) => x === oldValue ? newValue : x))}
            placeholder={t.servicePlaceholder}
            disabled={disabled}
          />
        </div>
        <div style={{ ...fieldWrap, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="checkbox" id="sup-ppd"
            checked={form.personalDataProcessed}
            onChange={(e) => sf('personalDataProcessed')(e.target.checked)}
            disabled={disabled}
            style={{ width: 16, height: 16, flexShrink: 0 }}
          />
          <label htmlFor="sup-ppd" style={{ ...labelSt, marginBottom: 0, textTransform: 'none', letterSpacing: 0, fontSize: '0.82rem', cursor: 'pointer' }}>
            {t.fPersonalDataProcessed}
          </label>
        </div>
        {form.personalDataProcessed && (
          <>
            <div style={fieldWrap}>
              <label style={labelSt}>{t.fDataProcessed}</label>
              <TagInput
                tags={form.dataProcessed}
                onAdd={(v) => sf('dataProcessed')([...form.dataProcessed, v])}
                onRemove={(v) => sf('dataProcessed')(form.dataProcessed.filter((x) => x !== v))}
                onUpdate={(oldValue, newValue) => sf('dataProcessed')(form.dataProcessed.map((x) => x === oldValue ? newValue : x))}
                placeholder={t.dataTypePlaceholder}
                disabled={disabled}
              />
            </div>
            <div style={grid2}>
              <div style={fieldWrap}>
                <label style={labelSt}>
                  {t.fDataProcessingPurpose}
                  <InfoBadge text={t.ttDataProcessingPurpose} />{' '}
                  <span style={{ ...FONT_TINY, color: TEXT_DISABLED, textTransform: 'none', letterSpacing: 0 }}>
                    {t.optional}
                  </span>
                </label>
                <input value={form.dataProcessingPurpose} onChange={hic('dataProcessingPurpose')} placeholder={t.purposePlaceholder} disabled={disabled} style={inputSt(false)} />
              </div>
              <div style={fieldWrap}>
                <label style={labelSt}>
                  {t.fTransferMechanism}
                  <InfoBadge text={t.ttTransferMechanism} />{' '}
                  <span style={{ ...FONT_TINY, color: TEXT_DISABLED, textTransform: 'none', letterSpacing: 0 }}>
                    {t.optional}
                  </span>
                </label>
                <input value={form.transferMechanism} onChange={hic('transferMechanism')} placeholder={t.transferPlaceholder} disabled={disabled} style={inputSt(false)} />
              </div>
            </div>
          </>
        )}
        <div style={{ ...fieldWrap, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="checkbox" id="sup-sri"
            checked={form.securityRequirementsIncluded}
            onChange={(e) => sf('securityRequirementsIncluded')(e.target.checked)}
            disabled={disabled}
            style={{ width: 16, height: 16, flexShrink: 0 }}
          />
          <label htmlFor="sup-sri" style={{ ...labelSt, marginBottom: 0, textTransform: 'none', letterSpacing: 0, fontSize: '0.82rem', cursor: 'pointer' }}>
            {t.fSecurityReq}
            <InfoBadge text={t.ttSecurityReq} />
          </label>
        </div>
      </div>

      {/* ── Section 4: Contract ────────────────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}>{t.s4}</h3>
        <div style={{ ...fieldWrap, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="checkbox" id="sup-ce"
            checked={form.contractExists}
            onChange={(e) => sf('contractExists')(e.target.checked)}
            disabled={disabled}
            style={{ width: 16, height: 16, flexShrink: 0 }}
          />
          <label htmlFor="sup-ce" style={{ ...labelSt, marginBottom: 0, textTransform: 'none', letterSpacing: 0, fontSize: '0.82rem', cursor: 'pointer' }}>
            {t.fContractExists}
          </label>
        </div>
        <div style={{ ...fieldWrap, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="checkbox" id="sup-nda"
            checked={form.ndaSigned}
            onChange={(e) => sf('ndaSigned')(e.target.checked)}
            disabled={disabled}
            style={{ width: 16, height: 16, flexShrink: 0 }}
          />
          <label htmlFor="sup-nda" style={{ ...labelSt, marginBottom: 0, textTransform: 'none', letterSpacing: 0, fontSize: '0.82rem', cursor: 'pointer' }}>
            {t.fNdaSigned}
          </label>
        </div>
        <div style={{ ...fieldWrap, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="checkbox" id="sup-dpa"
            checked={form.dpaSigned}
            onChange={(e) => sf('dpaSigned')(e.target.checked)}
            disabled={disabled}
            style={{ width: 16, height: 16, flexShrink: 0 }}
          />
          <label htmlFor="sup-dpa" style={{ ...labelSt, marginBottom: 0, textTransform: 'none', letterSpacing: 0, fontSize: '0.82rem', cursor: 'pointer' }}>
            {t.fDpaSigned}
            <InfoBadge text={t.ttDpaSigned} />
          </label>
        </div>
        <div style={{
          ...fieldWrap, display: 'flex', alignItems: 'center', gap: '0.5rem',
          ...(form.criticality === 'Critical' ? {
            padding: '0.5rem 0.75rem',
            border: `1px solid ${ERROR_BORDER}`,
            background: ERROR_BG,
            borderRadius: 6,
          } : {}),
        }}>
          <input
            type="checkbox" id="sup-audit"
            checked={form.auditRights}
            onChange={(e) => sf('auditRights')(e.target.checked)}
            disabled={disabled}
            style={{ width: 16, height: 16, flexShrink: 0 }}
          />
          <label htmlFor="sup-audit" style={{
            ...labelSt, marginBottom: 0, textTransform: 'none', letterSpacing: 0,
            fontSize: '0.82rem', cursor: 'pointer',
            color: form.criticality === 'Critical' ? ERROR_TEXT : labelSt.color,
            fontWeight: form.criticality === 'Critical' ? 600 : 'normal',
          }}>
            {t.fAuditRights}
            <InfoBadge text={t.ttAuditRights} />
            {form.criticality === 'Critical' && (
              <span style={{ ...FONT_SMALL, marginLeft: '0.5rem', color: ERROR_TEXT }}>
                · {t.fAuditRightsCritical}
              </span>
            )}
          </label>
        </div>
        <div style={grid2}>
          <div style={fieldWrap}>
            <label style={labelSt}>
              {t.fContractRef}{' '}
              <span style={{ ...FONT_TINY, color: TEXT_DISABLED, textTransform: 'none', letterSpacing: 0 }}>
                {t.optional}
              </span>
            </label>
            <input value={form.contractRef} onChange={hic('contractRef')} disabled={disabled} style={inputSt(false)} />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>
              {t.fContractStart}{' '}
              <span style={{ ...FONT_TINY, color: TEXT_DISABLED, textTransform: 'none', letterSpacing: 0 }}>
                {t.optional}
              </span>
            </label>
            <input type="date" value={form.contractStartDate} onChange={hic('contractStartDate')} disabled={disabled} style={inputSt(false)} />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>
              {t.fContractExpiry}{' '}
              <span style={{ ...FONT_TINY, color: TEXT_DISABLED, textTransform: 'none', letterSpacing: 0 }}>
                {t.optional}
              </span>
            </label>
            <input type="date" value={form.contractExpiry} onChange={hic('contractExpiry')} disabled={disabled} style={inputSt(false)} />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>
              {t.fIncidentNotificationSla}{' '}
              <span style={{ ...FONT_TINY, color: TEXT_DISABLED, textTransform: 'none', letterSpacing: 0 }}>
                {t.optional}
              </span>
            </label>
            <input
              type="number" min="0"
              value={form.incidentNotificationSlaHours}
              onChange={hic('incidentNotificationSlaHours')}
              placeholder={t.slaPlaceholder}
              disabled={disabled}
              style={inputSt(false)}
            />
          </div>
        </div>
        <div style={fieldWrap}>
          <label style={labelSt}>
            {t.fCertifications}{' '}
            <span style={{ ...FONT_TINY, color: TEXT_DISABLED, textTransform: 'none', letterSpacing: 0 }}>
              {t.optional}
            </span>
          </label>
          <TagInput
            tags={form.certifications}
            onAdd={(v) => sf('certifications')([...form.certifications, v])}
            onRemove={(v) => sf('certifications')(form.certifications.filter((x) => x !== v))}
            onUpdate={(oldValue, newValue) => sf('certifications')(form.certifications.map((x) => x === oldValue ? newValue : x))}
            placeholder={t.certPlaceholder}
            disabled={disabled}
          />
        </div>
      </div>

      {/* ── Section 5: Review ──────────────────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}>{t.s5}</h3>
        <div style={grid2}>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fReviewFrequency}</label>
            <input
              type="number" min="1" max="60"
              value={form.reviewFrequencyMonths}
              onChange={(e) => sf('reviewFrequencyMonths')(e.target.value)}
              disabled={disabled}
              style={inputSt(false)}
            />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>
              {t.fLastReviewDate}{' '}
              <span style={{ ...FONT_TINY, color: TEXT_DISABLED, textTransform: 'none', letterSpacing: 0 }}>
                {t.optional}
              </span>
            </label>
            <input type="date" value={form.lastReviewDate} onChange={hic('lastReviewDate')} disabled={disabled} style={inputSt(false)} />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>
              {t.fLastReviewOutcome}{' '}
              <span style={{ ...FONT_TINY, color: TEXT_DISABLED, textTransform: 'none', letterSpacing: 0 }}>
                {t.optional}
              </span>
            </label>
            <select value={form.lastReviewOutcome} onChange={hic('lastReviewOutcome')} disabled={disabled} style={selectSt}>
              <option value="">— {t.noOutcome} —</option>
              {REVIEW_OUTCOMES.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fNextReviewDate}</label>
            <input
              type="date"
              value={form.nextReviewDate}
              readOnly
              style={{ ...inputSt(false), background: BG_CONTENT, color: TEXT_MUTED, cursor: 'default' }}
            />
          </div>
        </div>
      </div>

      {/* ── Section 6: Relationships ──────────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}>{t.s6}</h3>
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fEvidenceIds}</label>
          <TagInput
            tags={form.evidenceIds}
            onAdd={(v) => sf('evidenceIds')([...form.evidenceIds, v])}
            onRemove={(v) => sf('evidenceIds')(form.evidenceIds.filter((x) => x !== v))}
            onUpdate={(oldValue, newValue) => sf('evidenceIds')(form.evidenceIds.map((x) => x === oldValue ? newValue : x))}
            placeholder={t.evidencePlaceholder}
            disabled={disabled}
          />
        </div>
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fSubprocessorIds}</label>
          {supplierOptions.length > 0 ? (
            <EntitySelector
              items={supplierOptions.filter((o) => o.id !== fileId)}
              selectedIds={form.subprocessorIds}
              onAdd={(id) => sf('subprocessorIds')([...form.subprocessorIds, id])}
              onRemove={(id) => sf('subprocessorIds')(form.subprocessorIds.filter((x) => x !== id))}
              placeholder={t.subprocessorPlaceholder}
              disabled={disabled}
              chipBg={STATUS_INFO.bg}
              chipColor="#1d4ed8"
            />
          ) : (
            <TagInput
              tags={form.subprocessorIds}
              onAdd={(v) => sf('subprocessorIds')([...form.subprocessorIds, v])}
              onRemove={(v) => sf('subprocessorIds')(form.subprocessorIds.filter((x) => x !== v))}
              onUpdate={(oldValue, newValue) => sf('subprocessorIds')(form.subprocessorIds.map((x) => x === oldValue ? newValue : x))}
              placeholder={t.subprocessorPlaceholder}
              disabled={disabled}
            />
          )}
        </div>
      </div>

      {/* ── Section 7: Lifecycle ───────────────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}>{t.s7}</h3>
        <div style={grid2}>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fStatus} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
            <select value={form.status} onChange={hic('status')} disabled={disabled} style={selectSt}>
              {SUPPLIER_STATUSES.map((v) => <option key={v} value={v}>{t.statusLabels[v]}</option>)}
            </select>
          </div>
          <div style={{ ...fieldWrap, display: 'flex', alignItems: 'center', gap: '0.5rem', gridColumn: 'span 2' }}>
            <input
              type="checkbox" id="sup-epe"
              checked={form.exitPlanExists}
              onChange={() => sf('exitPlanExists')(!form.exitPlanExists)}
              disabled={disabled}
              style={{ width: 16, height: 16, flexShrink: 0 }}
            />
            <label htmlFor="sup-epe" style={{ ...labelSt, marginBottom: 0, textTransform: 'none', letterSpacing: 0, fontSize: '0.82rem', cursor: 'pointer' }}>
              {t.fExitPlanExists}
              <InfoBadge text={t.ttExitPlanExists} />
            </label>
          </div>
          <div style={{ ...fieldWrap, gridColumn: 'span 2' }}>
            <label style={labelSt}>{t.fNotes}</label>
            <textarea value={form.notes} onChange={hic('notes')} disabled={disabled} style={textareaSt(false)} />
          </div>
        </div>
      </div>

      </fieldset>

      {/* ── Sticky action bar ─────────────────────────────────────────────── */}
      {!isView && (
        <div style={{
          position: 'sticky',
          bottom: 0,
          marginLeft: '-2rem',
          marginRight: '-2rem',
          marginBottom: '-2rem',
          marginTop: '1.5rem',
          padding: '0.85rem 2rem',
          background: BG_SURFACE,
          borderTop: `1px solid ${BORDER_DEFAULT}`,
          borderBottomLeftRadius: 12,
          borderBottomRightRadius: 12,
          zIndex: 10,
        }}>
          {saveState === 'error' && (
            <div style={{ ...FONT_LABEL, background: ERROR_BG, color: ERROR_TEXT, border: `1px solid ${ERROR_BORDER}`, borderRadius: 6, padding: '0.35rem 0.75rem', marginBottom: '0.5rem' }}>
              {t.errorSave}
            </div>
          )}
          {deleteError && (
            <p style={{ ...FONT_LABEL, color: ERROR_TEXT, margin: '0 0 0.5rem' }}>
              {deleteError}
            </p>
          )}
          {Object.keys(errors).length > 0 && (
            <p style={{ ...FONT_SMALL, color: ERROR_TEXT, margin: '0 0 0.5rem' }}>
              {t.errorRequired}
            </p>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
            <div>
              {currentMode === 'edit' && (
                <button
                  onClick={() => setConfirmState({ confirmLabel: t.btnDelete })}
                  disabled={disabled}
                  style={{ ...BTN_DESTRUCTIVE_OUTLINE, ...FONT_SMALL, borderRadius: 8, padding: '0.5rem 1rem', cursor: disabled ? 'not-allowed' : 'pointer' }}
                >
                  {t.btnDelete}
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              {dirty && (
                <span style={{ ...FONT_LABEL, color: UNSAVED_AMBER }}>{t.unsavedLabel}</span>
              )}
              <button onClick={onCancel} disabled={disabled} style={btnSecondary}>{t.btnCancel}</button>
              <button onClick={handleSave} disabled={disabled} style={btnPrimary}>{saveState === 'saving' ? t.saving : saveState === 'ok' ? t.saveOk : t.btnSave}</button>
            </div>
          </div>
        </div>
      )}
      <SaveErrorDialog
        open={saveErrorOpen}
        lang={lang}
        onRetry={handleRetrySave}
        onDiscard={handleDiscardChanges}
      />
    </div>
  );
}
